import { createTheme, type ThemeOptions } from '@mui/material/styles'
import { getCompassThemeOptions } from '@simplisafe/compass-web'

// The type cast is necessary below because compass-web is resolved via a local `link:` dependency
// during development, which gives it its own physical copy of @mui/material distinct from this
// app's copy. TypeScript treats the two copies as nominally incompatible even though they're the
// same version, so we assert the shape here.
const compassThemeOptions = getCompassThemeOptions('midnight') as ThemeOptions

const overrides = {
  components: {
    MuiStack: {
      defaultProps: {
        useFlexGap: true
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        slotProps: {
          inputLabel: {
            shrink: true
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          display: 'block',
          marginBottom: 'var(--spacing-2xs)',
          pointerEvents: 'auto'
        }
      }
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      },
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          maxWidth: '100%',
          lineHeight: 'var(--line-height-xs)',
          pointerEvents: 'auto',
          transition: 'none',
          color: 'var(--text-primary)',
          fontWeight: 500,
          letterSpacing: 'var(--letter-spacing-xs)',
          fontSize: 'var(--size-xs)'
        },
        formControl: {
          position: 'static',
          transform: 'none'
        },
        shrink: {
          transform: 'none'
        },
        animated: {
          transition: 'none'
        },
        outlined: {
          transform: 'none'
        },
        filled: {
          transform: 'none'
        },
        standard: {
          transform: 'none'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 'var(--spacing-2xs)',
          marginLeft: 'var(--spacing-none)'
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            border: `1px solid var(--button-primary)`
          },
          '& .MuiPaginationItem-root': {
            color: 'var(--text-primary)'
          },
          '& .MuiPaginationItem-page': {
            color: 'var(--text-primary)',
            backgroundColor: 'transparent',
            border: 'none'
          },
          '& .MuiPaginationItem-page.Mui-selected': {
            backgroundColor: 'transparent',
            color: 'primary.main',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: '50%'
          }
        },
        page: {
          backgroundColor: 'transparent',
          border: 'none',
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: 'var(--button-primary)',
            border: '1px solid',
            borderColor: 'var(--button-primary)',
            borderRadius: '50%'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            marginTop: 0
          },
          backgroundColor: 'var(--bg-surface)',
          color: 'var(--text-secondary)'
        },
        input: {
          fontSize: 'var(--size-s)',
          '&.large-dots': {
            WebkitTextSecurity: 'disc',
            fontSize: 'var(--size-2xl)',
            lineHeight: 1,
            letterSpacing: 'var(--letter-spacing-s)'
          }
        }
      }
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false
      },
      styleOverrides: {
        notchedOutline: {
          '& legend': {
            width: '0'
          }
        }
      }
    },
    MuiPickersOutlinedInput: {
      defaultProps: {
        notched: false
      },
      styleOverrides: {
        notchedOutline: {
          '& legend': {
            width: '0'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--border-radius-l)',
          fontSize: 'var(--size-s)',
          paddingLeft: 'var(--spacing-2xs)',
          paddingRight: 'var(--spacing-2xs)',
          gap: 'var(--spacing-2xs)',

          // default state is gray with white text
          backgroundColor: 'var(--button-subtle)'
        },
        colorWarning: {
          backgroundColor: 'var(--bg-surface-warning)',
          color: 'var(--button-secondary-fg)'
        },
        label: {
          padding: 0
        },
        icon: {
          margin: 0
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          // TableCell text inside TableHead should never wrap
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '.MuiTabs-indicator': {
            backgroundColor: 'var(--bg-surface-info)'
          },
          minHeight: 'unset'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: 'var(--text-info)'
          },
          color: 'var(--text-secondary)',
          textTransform: 'none',
          padding: 'var(--spacing-xs) var(--spacing-s)',
          fontSize: 'var(--size-m)'
        }
      }
    },
    MuiButton: {
      defaultProps: {
        variant: 'outlined'
      },
      styleOverrides: {
        root: {
          padding: 'var(--spacing-s) var(--spacing-m)',
          borderRadius: 'var(--border-radius-round)',
          textTransform: 'none'
        }
      }
    },
    MuiBox: {
      styleOverrides: {
        root: {
          fontSize: 'var(--size-s)'
        }
      }
    },
    MuiCircularProgress: {
      defaultProps: {
        enableTrackSlot: true,
        size: '4.5rem',
        thickness: 4.5
      },
      styleOverrides: {
        root: {
          color: 'var(--shape-medium)'
        }
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': {
            color: 'var(--text-disabled)'
          }
        }
      }
    },
    MuiPopover: {
      defaultProps: {
        container: () => document.getElementById('color-mode-container') ?? undefined
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'var(--link-default)',
          '&:active': {
            color: 'var(--link-pressed)'
          },
          '&.Mui-disabled': {
            color: 'var(--link-disabled)'
          }
        }
      }
    },
    MuiTooltip: {
      defaultProps: {
        PopperProps: {
          container: () => document.getElementById('color-mode-container') ?? undefined
        }
      },
      styleOverrides: {
        tooltip: {
          fontSize: 'var(--size-xs)',
          lineHeight: 'var(--line-height-xs)',
          backgroundColor: 'var(--shape-low)',
          color: 'var(--text-primary)',
          maxWidth: 'none'
        },
        arrow: {
          color: 'var(--shape-low)'
        }
      }
    }
  }
}

export const theme = createTheme(compassThemeOptions, overrides)
